import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { notification, theme, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import {
  ControlAttestationRow,
  QueryScope
} from 'store/ducks/InternalControls/types';
import { internalControlsOp } from 'store/ducks/InternalControls';
import { useLocation } from 'react-router-dom';
import { useQueryParams, withDefault, ArrayParam } from 'use-query-params';
import qs from 'qs';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import { getControlAttestationColumns } from 'constants/controlAttestationColumns';
import * as Styled from '../../RiskControl.styled';
import * as LayoutStyled from 'components/Layouts/Layout.styled';

const { useToken } = theme;

const ControlAttestationTesting: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { drawTable, selectedEntityId, lang } = useSelector(
    (state: RootState) => ({
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId,
      lang: state.global.lang
    })
  );

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [controls, setControls] = useState<ControlAttestationRow[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [newControlIds, setNewControlIds] = useState<Set<number>>(new Set());
  const originalControlsRef = useRef<ControlAttestationRow[]>([]);
  const dispatch = useDispatch();

  // Simple tracking function that marks data as dirty
  const trackingSetControls = (
    newControls:
      | ControlAttestationRow[]
      | ((prev: ControlAttestationRow[]) => ControlAttestationRow[])
  ) => {
    if (typeof newControls === 'function') {
      setControls((prev) => {
        const result = newControls(prev);
        setIsDirty(true);
        return result;
      });
    } else {
      setControls(newControls);
      setIsDirty(true);
    }
  };

  useEffect(() => {
    dispatch(
      internalControlsOp.setHasChanges(isDirty || newControlIds.size > 0)
    );
  }, [isDirty, newControlIds, dispatch]);

  const [designEffectiveness, setDesignEffectiveness] = useState<
    { label: string; value: number }[]
  >([]);

  const [testResult, setTestResult] = useState<
    { label: string; value: number }[]
  >([]);

  const location = useLocation();

  const queryParams = qs.parse(location.search, {
    ignoreQueryPrefix: true
  });

  const [testingMethod, setTestingMethod] = useState<
    { label: string; value: number }[]
  >([]);

  const [testType, setTestType] = useState<{ label: string; value: number }[]>(
    []
  );

  const [evidenceType, setEvidenceType] = useState<
    { label: string; value: number }[]
  >([]);

  const [controlAttestationEffectiveness, setControlAttestationEffectiviness] =
    useState<{ label: string; value: number }[]>([]);

  const [testStatus, setTestStatus] = useState<
    { label: string; value: number }[]
  >([]);
  const { token } = useToken();
  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));

  const FiltersParam = withDefault(ArrayParam, []);

  const [query, setQuery] = useQueryParams({
    group: FiltersParam,
    division: FiltersParam,
    region: FiltersParam,
    legal_entity: FiltersParam,
    business_unit: FiltersParam,
    control_owner: FiltersParam,
    bru_assigned: FiltersParam
  });

  const bruAssignedFilter = (query.bru_assigned?.[0] as string) || undefined;

  const getValidIdFromArray = (value: unknown): number | undefined => {
    if (!Array.isArray(value) || value.length === 0) return undefined;

    const num = Number(value[0]);
    return Number.isInteger(num) && num > 0 ? num : undefined;
  };

  const buildSingleControlParamFromQuery = () => {
    const params: Record<string, number> = {};

    const groupId = getValidIdFromArray(query.group);
    if (groupId !== undefined) {
      params.group_id = groupId;
    } else {
      const divisionId = getValidIdFromArray(query.division);
      if (divisionId !== undefined) {
        params.division_id = divisionId;
      } else {
        const regionId = getValidIdFromArray(query.region);
        if (regionId !== undefined) {
          params.region_id = regionId;
        } else {
          const legalEntityId = getValidIdFromArray(query.legal_entity);
          if (legalEntityId !== undefined) {
            params.legal_entity_id = legalEntityId;
          } else {
            const businessUnitId = getValidIdFromArray(query.business_unit);
            if (businessUnitId !== undefined) {
              params.business_unit_id = businessUnitId;
            }
          }
        }
      }
    }

    const controlOwnerId = getValidIdFromArray(query.control_owner);
    if (controlOwnerId !== undefined) {
      params.control_owner_id = controlOwnerId;
    }

    return params;
  };

  const scopeKey = useMemo(() => {
    const scope = buildSingleControlParamFromQuery();
    return JSON.stringify(scope);
  }, [query]);

  useEffect(() => {
    const fetchControls = async () => {
      try {
        const params = buildSingleControlParamFromQuery();
        if (bruAssignedFilter) {
          (params as any).bru_assigned = bruAssignedFilter;
        }
        const response = await ApiService.getControlsAttestationBPR(params);
        const apiControls = response.data.data;
        const mappedControls = apiControls.map((item: any) => {
          const instance = item.instances?.[0] ?? {};

          return {
            key: item.id.toString(),
            id: item.id,

            // ───────── CONTROL LEVEL ─────────
            controlName: item.control_name,
            controlDescription: item.control_description,
            start_date: item.start_date,
            end_date: item.end_date,
            notUsed: item.not_used === true,
            keyControl: item.key_control || false,
            testingMethod: item.testing_method_id_3,
            testType: {
              id: item.test_type.id ?? null,
              name: item.test_type.name ?? null
            },
            // ───────── INSTANCE LEVEL ─────────
            instance_id: instance.instance_id ?? null,

            group: instance.group ?? null,
            division: instance.division ?? null,
            region: instance.region ?? null,
            legal_entity: instance.legal_entity ?? null,
            business_unit: instance.business_unit ?? null,

            performer_toe: instance.performer_toe ?? null,
            approver: instance.approver ?? null,

            next_test_date: instance.next_test_date ?? null,
            test_objectiveness_3: instance.test_objectiveness_3 ?? null,
            test_description_3: instance.test_description_3,
            evidence_description_3: instance.evidence_description_3,
            evidence_file_1: instance.evidence_file_3,
            description: instance.description_3 ?? null,

            testResult: instance.test_result ?? null,
            evidenceType: instance.evidence_type ?? null,
            controlAttestationEffectiveness:
              instance.control_attestation_effectiveness ?? null,

            testStatus: instance.test_status ?? null,
            control_effectiveness: instance.control_effectiveness ?? null
          };
        });

        setControls(mappedControls);
        originalControlsRef.current = JSON.parse(
          JSON.stringify(mappedControls)
        );
        setIsDirty(false);

        dispatch(internalControlsOp.setDrawTable(false));
      } catch (error) {
        console.error('Error fetching controls:', error);
      }
    };

    fetchControls();
  }, [drawTable, dispatch, scopeKey, bruAssignedFilter]);

  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [
          designEffRes,
          testingMethodRes,
          testTypeRes,
          evidenceTypeRes,
          testResultRes,
          controlAttestationEffectivenessRes,
          testStatusRes
        ] = await Promise.all([
          ApiService.getDesignEffectivenessBPR(),
          ApiService.getTestingMethodBPR(),
          ApiService.getTestTypesBPR(),
          ApiService.getEvidenceTypeBPR(),
          ApiService.getTestResultBPR(),
          ApiService.getControlAttestationEffectivenessBPR(),
          ApiService.getTestStatusesBPR()
        ]);

        setDesignEffectiveness(
          designEffRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestingMethod(
          testingMethodRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestType(
          testTypeRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setEvidenceType(
          evidenceTypeRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestResult(
          testResultRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setControlAttestationEffectiviness(
          controlAttestationEffectivenessRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );

        setTestStatus(
          testStatusRes.data.data.map((o: any) => ({
            label: o.name,
            value: o.id
          }))
        );
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };

    fetchAllDropdowns();
  }, []);

  const buildNormalizedScope = (): QueryScope => {
    const singleScope = buildSingleControlParamFromQuery();
    return {
      group_id: singleScope.group_id ?? null,
      division_id: singleScope.division_id ?? null,
      region_id: singleScope.region_id ?? null,
      legal_entity_id: singleScope.legal_entity_id ?? null,
      business_unit_id: singleScope.business_unit_id ?? null
    };
  };

  const isScopedView = useMemo(() => {
    const scope = buildSingleControlParamFromQuery();
    return Object.keys(scope).length > 0;
  }, [query]);

  const controlColumns = useMemo(
    () =>
      getControlAttestationColumns(
        testingMethod,
        controls,
        designEffectiveness,
        setControls,
        trackingSetControls,
        testType,
        evidenceType,
        testResult,
        controlAttestationEffectiveness,
        testStatus,
        newControlIds,
        isScopedView,
        t
      ),
    [
      testingMethod,
      controls,
      designEffectiveness,
      testType,
      evidenceType,
      testResult,
      controlAttestationEffectiveness,
      testStatus,
      setControls,
      trackingSetControls,
      newControlIds,
      isScopedView,
      t
    ]
  );

  const preparePayload = (
    controlsPerRow: ControlAttestationRow[],
    scope: QueryScope
  ) => {
    const hasScope = Object.values(scope).some(
      (value) => value !== null && value !== undefined
    );

    return {
      data_list: controlsPerRow.map((ctrl) => {
        const basePayload = {
          id: ctrl.id,
          control_name: ctrl.controlName ?? null,
          control_description: ctrl.controlDescription ?? null,

          control_image: (ctrl as any).control_image ?? null,

          start_date: ctrl.start_date ?? null,
          end_date: ctrl.end_date ?? null,

          testing_method_3:
            typeof ctrl.testingMethod === 'object'
              ? ctrl.testingMethod?.id ?? null
              : ctrl.testingMethod ?? null,

          test_type_id:
            typeof ctrl.testType === 'object'
              ? ctrl.testType?.id ?? null
              : ctrl.testType ?? null,

          updated_at: (ctrl as any).updated_at ?? null,

          active: (ctrl as any).active ?? true,
          deleted: (ctrl as any).deleted ?? false,

          comments: (ctrl as any).comments ?? null
        };

        // if (!hasScope) {
        //   return basePayload;
        // }
        return {
          ...basePayload,

          instance: {
            // ───────── SCOPE ─────────
            group: scope.group_id ?? null,
            division: scope.division_id ?? null,
            region: scope.region_id ?? null,
            legal_entity: scope.legal_entity_id ?? null,
            business_unit: scope.business_unit_id ?? null,

            // ───────── PEOPLE ─────────
            performer_toe: ctrl.performer_toe,
            approver: ctrl.approver,
            // ───────── DATES ─────────
            last_test_date_operational: ctrl.last_test_date_operational ?? null,
            next_test_date: ctrl.next_test_date ?? null,

            // ───────── TESTING FIELDS ─────────
            test_description_3: ctrl.test_description_3 ?? null,
            description_3: ctrl.description_3 ?? null,
            test_objectiveness_3: ctrl.test_objectiveness_3 ?? null,

            test_result:
              typeof ctrl.testResult === 'object'
                ? ctrl.testResult?.id ?? null
                : ctrl.testResult ?? null,

            evidence_type:
              typeof ctrl.evidenceType === 'object'
                ? ctrl.evidenceType?.id ?? null
                : ctrl.evidenceType ?? null,

            evidence_description_3: ctrl.evidence_description_3 ?? null,
            evidence_file_3: ctrl.evidence_file_3 ?? null,

            control_attestation_effectiveness:
              typeof ctrl.controlAttestationEffectiveness === 'object'
                ? ctrl.controlAttestationEffectiveness?.id ?? null
                : ctrl.controlAttestationEffectiveness ?? null,

            test_status:
              typeof ctrl.testStatus === 'object'
                ? ctrl.testStatus?.id ?? null
                : ctrl.testStatus ?? null,

            control_effectiveness:
              typeof ctrl.control_effectiveness === 'object'
                ? ctrl.control_effectiveness?.id ?? null
                : ctrl.control_effectiveness ?? null,

            assertion_area: 1,

            // ───────── EXTRA INSTANCE FLAGS (from sample) ─────────
            active: (ctrl as any).instanceActive ?? true,
            deleted: (ctrl as any).instanceDeleted ?? false,
            comments: (ctrl as any).instanceComments ?? null
          }
        };
      })
    };
  };

  const handleControlSave = async () => {
    try {
      if (!isDirty && newControlIds.size === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }

      // Find changed controls by comparing with original
      const changedControls = controls.filter((control) => {
        if (newControlIds.has(Number(control.id))) return true;
        const original = originalControlsRef.current.find(
          (c) => c.id === control.id
        );
        if (!original) return false;

        // Check if control has actually changed
        return JSON.stringify(control) !== JSON.stringify(original);
      });

      if (changedControls.length === 0) {
        notification.info({
          message: 'No Changes',
          description: 'No changes detected to save.',
          placement: 'topRight',
          duration: 3
        });
        return;
      }
      const scope = buildNormalizedScope();
      const payload = preparePayload(changedControls, scope);
      const response = await ApiService.saveControlsAttestationBPR(payload);

      originalControlsRef.current = JSON.parse(JSON.stringify(controls));
      setNewControlIds(new Set());
      setIsDirty(false);
      dispatch(internalControlsOp.setHasChanges(false));
      dispatch(internalControlsOp.setDrawTable(true));

      notification.success({
        message: 'Controls Saved',
        description:
          response?.data?.message ||
          `${changedControls.length} control(s) saved successfully.`,
        placement: 'topRight',
        duration: 3
      });
    } catch (error: unknown) {
      console.error('Save error:', error);

      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string; error?: string } };
          }
        )?.response?.data?.message ||
        (
          error as {
            response?: { data?: { message?: string; error?: string } };
          }
        )?.response?.data?.error ||
        (error as Error)?.message ||
        'Unknown error occurred';
      const statusCode =
        (error as { response?: { status?: unknown } })?.response?.status ||
        'Unknown';

      notification.error({
        message: `Save Failed (${statusCode})`,
        description: errorMessage,
        placement: 'topRight',
        duration: 10
      });
    }
  };

  return (
    <InternalControlLayout onSave={handleControlSave}>
      <>
        <Styled.DesignEffectivenessWrapper>
          <CustomTable
            drawTable={true}
            rowKey='key'
            columns={controlColumns}
            dataSource={controls}
            itemsCount={controls.length}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: controls.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) =>
                `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page: number, size: number) => {
                setCurrentPage(page);
                setPageSize(size);
              }
            }}
            rowClassName={(record: { notUsed: boolean }) =>
              record.notUsed ? 'disabled-row has-row-tooltip' : ''
            }
            onRow={(record: { notUsed: boolean }) => {
              if (!record.notUsed) return {};

              return {
                'data-tooltip': t('DISABLED_DUE_TO_RISK_CONTROL_TYPE_CHANGE')
              };
            }}
            scroll={{ x: 2500 }}
          />
        </Styled.DesignEffectivenessWrapper>
      </>
    </InternalControlLayout>
  );
};

export default ControlAttestationTesting;
